import React, { useId } from 'react';
import { Button, ButtonProps } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import styled from 'styled-components';
import { Dialog, DialogProps } from './Dialog';

export type FormDialogProps = Omit<DialogProps, 'actions'> & {
  onSubmit: () => void;
  submitLabel: React.ReactNode;
  cancelLabel: React.ReactNode;
  isSubmitting: boolean;
  /** Extra condition beyond `isSubmitting` for disabling the submit button, e.g. "nothing selected yet". */
  submitDisabled?: boolean;
  submitColor?: ButtonProps['color'];
  cancelColor?: ButtonProps['color'];
};

/**
 * `Dialog` plus the footer/submit wiring every RHF-driven dialog form hand-rolls identically: a Cancel
 * button and a submit button, both disabled while submitting. Deliberately thin — it only standardizes
 * this wiring, not field layout or validation. See docs/design-system/components.md.
 */
export const FormDialog: React.FC<FormDialogProps> = ({
  onSubmit,
  submitLabel,
  cancelLabel,
  isSubmitting,
  submitDisabled,
  submitColor = 'primary',
  cancelColor = 'inherit',
  onClose,
  children,
  ...dialogProps
}) => {
  const formId = useId();

  const handleSubmit: React.FormEventHandler = (event) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <Dialog
      {...dialogProps}
      onClose={onClose}
      actions={
        <>
          <Button onClick={onClose} color={cancelColor} disabled={isSubmitting}>
            {cancelLabel}
          </Button>
          <LoadingButton
            type="submit"
            form={formId}
            loading={isSubmitting}
            disabled={submitDisabled}
            variant="contained"
            color={submitColor}
          >
            {submitLabel}
          </LoadingButton>
        </>
      }
    >
      <Form id={formId} onSubmit={handleSubmit}>
        {children}
      </Form>
    </Dialog>
  );
};

// `display: contents` so the form itself doesn't participate in `Dialog`'s content flex layout —
// only its children do, keeping the same gap/spacing as if they were direct children of `Dialog`.
const Form = styled.form`
  display: contents;
`;
